import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobal } from 'reactn';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { LOCATIONS } from '../../constants';
import { isPermitted } from '../../components/permission-control/permission-control';
import { updateFormDefinition } from '../../util/APIUtils';
import { EVENTS } from '../services/constants';
import { eventEmitter } from '../services/event-emitter-service';
import store, { showToast } from '../core/store';
import {
  FORM_SUBMISSIONS_PAGE_SIZE,
  buildFormSubmissionsActionsDropdownOptions,
  buildFormSubmissionsColumns,
  buildFormSubmissionsExportInput,
  buildFormSubmissionsTableSx,
  buildFormSubmissionsTdStyles,
  collectSortedDataKeysFromSubmissions,
  collectSubmissionFieldMetaFromDefinition,
  fetchFormSubmissionsPageData,
  getLocationFilterOptions,
  isFormIdPresent,
  mapFormSubmissionsToGridData,
  showFormSubmissionErrorToast,
} from './utils/form-submission.util';
import {
  exportFormSubmissionAsPdf,
  exportFormSubmissionsArchive,
} from './utils/form-export-pdf.util';
import {
  FORM_RESPONSE_PDF_EXPORT_ERROR,
  FORM_SUBMISSIONS_LOAD_ERROR,
  FORM_SUBMISSIONS_ZIP_EXPORT_ERROR,
} from './constants/form-submission.constants';
import { FORMS_UPDATE_ERROR, formEditRoute } from './constants/forms.constants';
import { mapApiFormDefinitionToForm } from './utils/form.util';
import { buildFormSourceRoute } from './utils/form-source.util';
import { useFormSourceHook } from './useFormSourceHook';
import { FORM_MANAGE, FORM_VIEW } from './constants/form.permission';

export const useFormSubmissionsHook = () => {
  const [global] = useGlobal();
  const navigate = useNavigate();
  const { formId } = useParams();
  const { source, returnRoute } = useFormSourceHook();
  const [formTitle, setFormTitle] = useState('');
  const [formDefinition, setFormDefinition] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  /** Bumps when global `LOCATIONS` is populated so location filter options stay in sync. */
  const [locationFilterRevision, setLocationFilterRevision] = useState(0);
  const [previewForm, setPreviewForm] = useState(null);

  const canViewForm = useMemo(() => {
    const { cbacConfigFlag, permissions, permissionIds } = global;
    return isPermitted(cbacConfigFlag, permissions, FORM_VIEW, permissionIds);
  }, [global]);

  const canManageForm = useMemo(() => {
    const { cbacConfigFlag, permissions, permissionIds } = global;
    return isPermitted(cbacConfigFlag, permissions, FORM_MANAGE, permissionIds);
  }, [global]);

  const bumpLocationFilterRevision = useCallback(() => {
    setLocationFilterRevision((n) => n + 1);
  }, []);

  const resetSubmissionsState = useCallback(() => {
    setFormDefinition(null);
    setSubmissions([]);
    setLoading(false);
  }, []);

  const handleLoadFailure = useCallback(() => {
    setFormTitle('');
    setFormDefinition(null);
    setSubmissions([]);
    store.dispatch(showToast(FORM_SUBMISSIONS_LOAD_ERROR));
  }, []);

  const fetchSubmissionsForForm = useCallback(async () => {
    const pageData = await fetchFormSubmissionsPageData(formId);
    setFormTitle(pageData.formTitle);
    setFormDefinition(pageData.formDefinition);
    setSubmissions(pageData.submissions);
    bumpLocationFilterRevision();
  }, [formId, bumpLocationFilterRevision]);

  const load = useCallback(async () => {
    if (!isFormIdPresent(formId)) {
      resetSubmissionsState();
      return;
    }
    setLoading(true);
    try {
      await fetchSubmissionsForForm();
    } catch {
      handleLoadFailure();
    } finally {
      setLoading(false);
    }
  }, [formId, resetSubmissionsState, fetchSubmissionsForForm, handleLoadFailure]);

  const dataKeys = useMemo(
    () => collectSortedDataKeysFromSubmissions(submissions),
    [submissions],
  );

  const fieldMeta = useMemo(
    () => collectSubmissionFieldMetaFromDefinition(formDefinition),
    [formDefinition],
  );

  const { signatureKeys, currencyMeta, selectboxKeys, surveyKeys, datetimeKeys } = fieldMeta;

  const handleExportSubmissions = useCallback(async () => {
    const exportInput = buildFormSubmissionsExportInput({
      submissions,
      formDefinition,
      formTitle,
    });
    try {
      await exportFormSubmissionsArchive(exportInput);
    } catch {
      showFormSubmissionErrorToast(FORM_SUBMISSIONS_ZIP_EXPORT_ERROR);
    }
  }, [submissions, formDefinition, formTitle]);

  const handleExportResponseToPdf = useCallback(
    async (submission) => {
      try {
        await exportFormSubmissionAsPdf({ submission, formDefinition, formTitle });
      } catch {
        showFormSubmissionErrorToast(FORM_RESPONSE_PDF_EXPORT_ERROR);
      }
    },
    [formDefinition, formTitle],
  );

  const submissionActionHandlers = useMemo(
    () => ({
      onExportPdf: handleExportResponseToPdf,
    }),
    [handleExportResponseToPdf],
  );

  const locationFilterOptions = useMemo(
    () => getLocationFilterOptions(LOCATIONS),
    // LOCATIONS is module-level; `locationFilterRevision` bumps when it is populated (event / load).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional invalidation, not a missing dep on LOCATIONS
    [locationFilterRevision],
  );

  const columns = useMemo(
    () => buildFormSubmissionsColumns(submissions, signatureKeys, locationFilterOptions),
    [submissions, signatureKeys, locationFilterOptions],
  );

  const gridData = useMemo(
    () =>
      mapFormSubmissionsToGridData(submissions, {
        currencyMeta,
        dataKeys,
        datetimeKeys,
        selectboxKeys,
        signatureKeys,
        submissionActionHandlers,
        surveyKeys,
      }),
    [
      submissions,
      dataKeys,
      signatureKeys,
      currencyMeta,
      selectboxKeys,
      surveyKeys,
      datetimeKeys,
      submissionActionHandlers,
    ],
  );

  const tdStyles = useMemo(() => buildFormSubmissionsTdStyles(columns), [columns]);

  const tableSx = useMemo(() => buildFormSubmissionsTableSx(tdStyles), [tdStyles]);

  const handleBackToForms = useCallback(() => {
    navigate(returnRoute);
  }, [navigate, returnRoute]);

  const formForActions = useMemo(() => mapApiFormDefinitionToForm(formDefinition), [formDefinition]);

  const clearPreviewForm = useCallback(() => {
    setPreviewForm(null);
  }, []);

  const handlePreviewForm = useCallback((form) => {
    setPreviewForm(form);
  }, []);

  const handleEditFormFromMenu = useCallback(
    (form) => {
      if (form?.id == null || form.id === '') {
        return;
      }
      navigate(buildFormSourceRoute(formEditRoute(form.id), source));
    },
    [navigate, source],
  );

  const handleToggleActiveFromMenu = useCallback(
    async (form) => {
      setLoading(true);
      try {
        await updateFormDefinition(form.id, { is_active: !form.active });
        await fetchSubmissionsForForm();
      } catch {
        store.dispatch(showToast(FORMS_UPDATE_ERROR));
      } finally {
        setLoading(false);
      }
    },
    [fetchSubmissionsForForm],
  );

  const formActionsDropdownOptions = useMemo(
    () =>
      buildFormSubmissionsActionsDropdownOptions(
        formForActions,
        {
          ...(canViewForm ? { onPreview: handlePreviewForm } : {}),
          ...(canManageForm
            ? {
                onEdit: handleEditFormFromMenu,
                onToggleActive: handleToggleActiveFromMenu,
              }
            : {}),
        },
      ),
    [
      formForActions,
      canViewForm,
      canManageForm,
      handlePreviewForm,
      handleEditFormFromMenu,
      handleToggleActiveFromMenu,
    ],
  );

  const primaryButtonProps = useMemo(
    () => ({
      text: 'Actions',
      endIcon: <ArrowDropDownIcon />,
      dropdownOptions: formActionsDropdownOptions,
      show: formActionsDropdownOptions.length > 0,
    }),
    [formActionsDropdownOptions],
  );

  const secondaryButtonProps = useMemo(
    () => ({
      text: 'Export CSV',
      variant: 'outlined',
      startIcon: <SaveAltIcon />,
      onClick: handleExportSubmissions,
      disabled: loading || submissions.length === 0,
    }),
    [handleExportSubmissions, loading, submissions.length],
  );

  const subscribeToLocationsFetched = useCallback(() => {
    eventEmitter.on(EVENTS.LOCATIONS_FETCHED, bumpLocationFilterRevision);
    return () => {
      eventEmitter.off(EVENTS.LOCATIONS_FETCHED, bumpLocationFilterRevision);
    };
  }, [bumpLocationFilterRevision]);

  useEffect(subscribeToLocationsFetched, [subscribeToLocationsFetched]);
  useEffect(() => {
    load();
  }, [load]);

  return {
    formId,
    formTitle,
    formForActions,
    columns,
    gridData,
    tdStyles,
    tableSx,
    pageSize: FORM_SUBMISSIONS_PAGE_SIZE,
    loading,
    refreshSubmissions: load,
    handleBackToForms,
    primaryButtonProps,
    secondaryButtonProps,
    previewForm,
    clearPreviewForm,
    handlePreviewForm,
    handleEditFormFromMenu,
    handleToggleActiveFromMenu,
  };
};
